// src/utils/submitSampleOrder.js
// ✅ STANDARDIZED: sampleOrders use createdBy / createdByName / createdByRole
// ✅ NO requestedBy*, NO legacy duplicates
// ✅ Cloud Functions handle pricing + approval + stock (recommended)

import { collection, serverTimestamp, doc, writeBatch } from "firebase/firestore";

function normalizeDateInput(value) {
  if (!value) return null;

  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return null;

    const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];

    return s;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  return String(value);
}

function uniqTrimLimit(arr, limit = 10) {
  const out = [];
  const seen = new Set();

  (Array.isArray(arr) ? arr : [])
    .map((x) => (typeof x === "string" ? x.trim() : x))
    .filter(Boolean)
    .forEach((x) => {
      if (!seen.has(x)) {
        seen.add(x);
        out.push(x);
      }
    });

  return out.slice(0, limit);
}

function normalizeRole(role) {
  const r = String(role || "").trim().toLowerCase();
  if (r === "owner") return "owner";
  if (r === "admin") return "admin";
  if (r === "enduser" || r === "end_user" || r === "end-user") return "enduser";
  if (r === "customer") return "customer";
  // default safe
  return "customer";
}

/**
 * ✅ Creates a sample order (request) with group-based visibility
 * ✅ No client-side pricing - Cloud Function handles that
 * ✅ No client-side stock decrement/restore - Cloud Function handles that
 */
export async function submitSampleOrder({
  db,
  companyId,

  // ✅ CANONICAL STANDARD (Requester)
  createdBy,
  createdByName,
  createdByRole = "customer",

  accountId,
  accountName,

  assignedToId,
  assignedToName,

  requestedDate,
  deliveryDate,

  notes,

  rawItems,
  inventory,

  groupMode = "single",
  kitId = null,

  groupIds = [],
  accountGroupIds = [],
  staffGroupId = null,

  clientRequestId = null,

  // ✅ SIMPLIFIED: Always "Pending", Cloud Function decides approval
  initialStatus = "Pending",
} = {}) {
  console.log("[submitSampleOrder] Starting with params:", {
    companyId,
    createdBy,
    accountId,
    itemCount: Array.isArray(rawItems) ? rawItems.length : 0,
    initialStatus,
  });

  if (!db) throw new Error("submitSampleOrder: missing db");
  if (!companyId) throw new Error("submitSampleOrder: missing companyId");
  if (!createdBy) throw new Error("submitSampleOrder: missing createdBy");
  if (!accountId) throw new Error("submitSampleOrder: missing accountId");

  const normRequestedDate = normalizeDateInput(requestedDate);
  const normDeliveryDate = normalizeDateInput(deliveryDate);

  if (!normRequestedDate) {
    throw new Error("Please choose a requested delivery date.");
  }

  const invById = new Map((inventory || []).map((i) => [i.id, i]));

  const items = (Array.isArray(rawItems) ? rawItems : [])
    .filter((i) => i && i.productId && Number(i.quantity) > 0)
    .map((i) => {
      const inv = invById.get(i.productId) || {};
      return {
        productId: i.productId,
        quantity: Number(i.quantity) || 1,
        productName: inv.name || null,
        sku: inv.sku || null,
      };
    });

  if (!items.length) {
    throw new Error("Please add at least one product to sample.");
  }

  const totalQuantity = items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);

  let productName;
  if (items.length === 1) {
    productName = items[0].productName || "Sample item";
  } else if (kitId) {
    productName = `Sample kit (${items.length} items)`;
  } else {
    const itemNames = items.map((i) => i.productName).filter(Boolean);
    if (itemNames.length <= 2) {
      productName = itemNames.join(" + ");
    } else {
      productName = `${itemNames.slice(0, 2).join(", ")} + ${itemNames.length - 2} more`;
    }
  }

  const resolvedGroupIds = (() => {
    const primary = Array.isArray(groupIds) && groupIds.length ? groupIds : null;
    const secondary =
      Array.isArray(accountGroupIds) && accountGroupIds.length ? accountGroupIds : null;

    const picked = primary || secondary || [];
    const deduped = uniqTrimLimit(picked, 10);

    if (!deduped.length && staffGroupId) return uniqTrimLimit([staffGroupId], 10);
    return deduped;
  })();

  console.log("[submitSampleOrder] Resolved groupIds:", resolvedGroupIds);

  const payload = {
    companyId,

    // ✅ CANONICAL STANDARD
    createdBy,
    createdByName: createdByName || null,
    createdByRole: normalizeRole(createdByRole),

    accountId,
    accountName: accountName || null,

    assignedToId: assignedToId || null,
    assignedToName: assignedToName || null,

    requestedDate: normRequestedDate,
    deliveryDate: normDeliveryDate || null,

    notes: notes || null,

    productId: items.length === 1 ? items[0].productId : null,
    productName,
    quantity: totalQuantity,
    items,

    kitId: kitId || null,
    groupMode: groupMode || "single",

    groupIds: resolvedGroupIds,

    // ✅ STOCK FLAGS: server updates these (Cloud Functions)
    stockDeducted: false,
    stockRestored: false,

    // ✅ ALWAYS Pending - server decides approval/pricing
    status: initialStatus || "Pending",

    clientRequestId: clientRequestId || null,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const batch = writeBatch(db);
  const requestRef = doc(collection(db, "companies", companyId, "sampleOrders"));

  console.log("[submitSampleOrder] Creating document at path:", requestRef.path);

  batch.set(requestRef, payload);

  try {
    await batch.commit();

    console.log(`✅ Sample order created with ID: ${requestRef.id}`);
    console.log("ℹ️ Cloud Functions will calculate pricing, handle auto-approval, and manage stock.");

    return {
      id: requestRef.id,
      refPath: requestRef.path,
      payload,
    };
  } catch (error) {
    console.error("[submitSampleOrder] ❌ Batch commit failed:", error);
    console.error("[submitSampleOrder] Error details:", {
      code: error.code,
      message: error.message,
      companyId,
      createdBy,
      path: requestRef.path,
    });
    throw error;
  }
}
